//! Location as a NAME, on the Employee page (Levi, 2026-10-26).
//!
//! `airtable_mirror.employee_j2as.location` is a JSON array of Airtable record
//! ids, one per linked location, and the page rendered it through `fmt()`, which
//! joins arrays with ", ". So the Location cell read
//! "recm9p7ou3zM4P0zA" — the recid — instead of "Houston".
//!
//! THE NAMES ARE NOT IN THE ROW PAYLOAD. Portal-BFF exposes `location` but not
//! its `location_text` twin (see EMPLOYEE_COLUMNS in
//! Portal-BFF/src/controllers/employeeAdminController.js), and that twin is an
//! Airtable rollup the nightly mirror copies, so it trails a rename. The
//! authoritative list is MySQL `portal_app.location` — GCP-owned, the same table
//! the "Update Rep Location" pickers read — which the page fetches once from
//! GET /employee-admin/portal/locations.
//!
//! So the option list is held HERE, in module scope, and read by both halves of
//! the field: `locationLabel` renders the cell, `getLocationOptions` fills the
//! picker. One list means the two cannot disagree about what a location is
//! called — which is the whole failure this file exists to prevent.
//!
//! Measured coverage (2026-10-26, prod read,
//! inbox/1a178d39ead26c0c/captures/mysql_9_employee_j2as.txt): all 1,468
//! employees holding a location hold one of that table's three
//! `airtable_recid`s, so resolution is total today. It is still written to
//! survive a miss — an unmapped id renders as itself rather than as a blank,
//! because a blank Location reads as "none set", which is a different and wrong
//! claim.
//!
//! ids and recids are NOT interchangeable. The picker posts the table's own `id`
//! (airtable_dependency_queue #159: an Airtable recid may not be an option id);
//! the row cache holds recids. `locationLinksForIds` and `locationIdsForRaw` are
//! the two directions, and they exist so no caller has to remember which is
//! which. Both are PLURAL (Levi, 2026-10-26 round 2): one position may hold
//! several locations, and the single-value pair this file shipped with could
//! only ever carry the first — a save then replaced a two-location employee's
//! pair with one link.

let byId = new Map();
let byRecId = new Map();
let optionList = [];

//! Replace the option list. Rows are { id, name, recid } as the BFF sends them;
//! a row with no name is dropped (it cannot label anything), and a row with a
//! NULL recid is kept — it is a real location that simply cannot be set yet, and
//! the BFF refuses that pick with a reason rather than silently clearing.
export const setLocationOptions = (rows) => {
  const list = [];
  const idMap = new Map();
  const recMap = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row) continue;
    const id = row.id === null || row.id === undefined ? "" : String(row.id);
    const name = row.name === null || row.name === undefined ? "" : String(row.name).trim();
    if (!id || !name) continue;
    const recid = row.recid ? String(row.recid) : null;
    const entry = { id, name, recid };
    list.push(entry);
    idMap.set(id, entry);
    if (recid) recMap.set(recid, entry);
  }
  optionList = list;
  byId = idMap;
  byRecId = recMap;
  return optionList;
};

export const getLocationOptions = () => optionList;

//! The recids a row holds, whatever shape they arrive in. mysql2 hands a JSON
//! column over as an array; the same column read through another client (or an
//! older cached payload) is the JSON text, and a hand-built row could be a
//! comma-joined string. All three mean the same thing.
export const normalizeLocationLinks = (raw) => {
  if (raw === null || raw === undefined || raw === "") return [];
  let parts;
  if (Array.isArray(raw)) {
    parts = raw;
  } else {
    const s = String(raw).trim();
    if (!s) return [];
    if (s.startsWith("[")) {
      try {
        const parsed = JSON.parse(s);
        parts = Array.isArray(parsed) ? parsed : [s];
      } catch {
        parts = [s];
      }
    } else {
      parts = s.split(",");
    }
  }
  const out = [];
  for (const p of parts) {
    const id = p === null || p === undefined ? "" : String(p).trim();
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
};

//! The names behind a row's links, in the row's own order. An id the option list
//! does not cover comes back as itself — see the header for why that beats "".
export const locationNames = (raw) =>
  normalizeLocationLinks(raw).map((recid) => byRecId.get(recid)?.name || recid);

export const locationLabel = (raw) => locationNames(raw).join(", ");

//! The picker's initial value: every option id behind the row's links, in the
//! row's own order. Two locations is a real state (3 employees on 2026-10-26)
//! and this is the half that makes it survive a save — opening the picker on
//! such a row checks BOTH, so the save that follows re-posts both.
//!
//! An id the option list does not cover is dropped rather than carried: a
//! picker can only post values it offers. That is the OPPOSITE of
//! `locationLabel`, which renders an unmapped id as itself, and the difference
//! is deliberate — the cell must not claim "none set", the picker must not
//! offer a value it cannot save. [] means "nothing to show", which leaves the
//! picker with no box checked rather than claiming a value the row lacks.
export const locationIdsForRaw = (raw) => {
  const out = [];
  for (const recid of normalizeLocationLinks(raw)) {
    const entry = byRecId.get(recid);
    if (entry && !out.includes(entry.id)) out.push(entry.id);
  }
  return out;
};

//! The recid array a row would hold after picking `ids` — the shape the row
//! cache stores, for the optimistic update. Accepts a bare id as well as an
//! array, so the one function serves both picker shapes.
//!
//! null in three cases, and they are the same null Portal-BFF writes: a blank
//! selection (the BFF stores NULL for an empty link, `mirrorLinkValue([])`),
//! an id the list does not cover, and a location with no Airtable record behind
//! it. The last two would 400 at the BFF and the optimistic patch is rolled
//! back; returning null here keeps the optimistic state from printing an id the
//! save is about to refuse.
export const locationLinksForIds = (ids) => {
  const list = Array.isArray(ids)
    ? ids
    : ids === null || ids === undefined || String(ids).trim() === ""
      ? []
      : [ids];
  if (!list.length) return null;
  const out = [];
  for (const id of list) {
    const entry = byId.get(id === null || id === undefined ? "" : String(id));
    if (!entry || !entry.recid) return null;
    if (!out.includes(entry.recid)) out.push(entry.recid);
  }
  return out;
};
